import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { TaskDetail, TaskStageView } from "@fieldpath/shared";
import { mdy } from "@fieldpath/shared";

import { api, type Fetcher } from "../api/client";
import { KindTag } from "./KindTag";

/**
 * A preview of a task, on hover, anywhere a task is listed.
 *
 * **The question it answers is "where is this up to", and until now the only
 * way to ask it was to open the task and come back.** On a screen listing
 * thirty tasks that is thirty round trips to learn something each row could
 * have told you.
 *
 * Three rules it must never break:
 *
 * - **It cannot catch a click.** `pointer-events: none`, and no buttons in it.
 *   A preview that intercepts the click meant for the row underneath is worse
 *   than no preview, because the row is the thing people actually want.
 * - **It appears only if you LINGER.** 350ms is roughly the line between
 *   sweeping the mouse across a list on the way somewhere and stopping to look
 *   at one row. Without the delay, crossing a list flashes a card per row.
 * - **It leaves immediately.** The delay is on the way in, never on the way
 *   out, because a card that outstays the cursor sits on top of what you moved
 *   to read.
 */

/**
 * Fetched task detail, kept for the life of the tab.
 *
 * **Module-level on purpose.** Scanning a list hovers the same rows over and
 * over, and a cache inside the component would be thrown away whenever the
 * screen re-rendered — turning one fetch per task into one per hover, which is
 * precisely the request storm this must not cause.
 */
const CACHE = new Map<string, TaskDetail>();

/** Fetches already in the air, so two quick hovers do not both ask. */
const PENDING = new Map<string, Promise<TaskDetail>>();

function load(fetcher: Fetcher, taskId: string): Promise<TaskDetail> {
  const cached = CACHE.get(taskId);
  if (cached) return Promise.resolve(cached);

  const already = PENDING.get(taskId);
  if (already) return already;

  const request = api
    .getTaskWorkflow(fetcher, taskId)
    .then((detail) => {
      CACHE.set(taskId, detail);
      return detail;
    })
    .finally(() => {
      PENDING.delete(taskId);
    });

  PENDING.set(taskId, request);
  return request;
}

/** 350ms: the line between crossing a list and stopping to read one row. */
const DWELL_MS = 350;

interface Anchor {
  readonly taskId: string;
  readonly title: string;
  readonly rect: DOMRect;
}

/**
 * Wire a screen for task previews.
 *
 * One call per screen. Spread `bind(id, title)` onto each row and render
 * `card` once at the root — the card is a portal, so where it is rendered in
 * the tree does not matter, only that it is rendered at all.
 */
export type TaskHoverBind = (
  taskId: string,
  title: string,
) => {
  onMouseEnter: (e: React.MouseEvent<HTMLElement>) => void;
  onMouseLeave: () => void;
};

export function useTaskHover(fetcher: Fetcher): {
  bind: TaskHoverBind;
  card: React.ReactNode;
} {
  const [anchor, setAnchor] = useState<Anchor | null>(null);
  const [detail, setDetail] = useState<TaskDetail | null>(null);
  const timer = useRef<number | null>(null);

  const cancel = useCallback(() => {
    if (timer.current !== null) {
      window.clearTimeout(timer.current);
      timer.current = null;
    }
  }, []);

  const hide = useCallback(() => {
    cancel();
    setAnchor(null);
    setDetail(null);
  }, [cancel]);

  // A card is positioned against a rectangle captured at hover time. Scroll,
  // and that rectangle is a lie -- so it goes. Cheaper and more honest than
  // tracking the row and re-measuring on every frame.
  useEffect(() => {
    if (!anchor) return;
    window.addEventListener("scroll", hide, true);
    window.addEventListener("resize", hide);
    return () => {
      window.removeEventListener("scroll", hide, true);
      window.removeEventListener("resize", hide);
    };
  }, [anchor, hide]);

  useEffect(() => cancel, [cancel]);

  const bind = useCallback(
    (taskId: string, title: string) => ({
      onMouseEnter: (e: React.MouseEvent<HTMLElement>) => {
        const rect = e.currentTarget.getBoundingClientRect();
        cancel();
        timer.current = window.setTimeout(() => {
          setAnchor({ taskId, title, rect });
          // Shown from the cache instantly on a second hover; otherwise the
          // title appears now and the rest fills in when the fetch lands.
          setDetail(CACHE.get(taskId) ?? null);
          void load(fetcher, taskId)
            .then((d) => {
              // Only if this is still the row being hovered. Moving on during
              // a fetch must not drop a stale card onto the new row.
              setAnchor((current) =>
                current && current.taskId === taskId ? current : current,
              );
              setDetail((prev) => (prev === null || prev.task.id === taskId ? d : prev));
            })
            .catch(() => {
              /* A preview that cannot load is a preview that shows its title. */
            });
        }, DWELL_MS);
      },
      onMouseLeave: hide,
    }),
    [cancel, fetcher, hide],
  );

  const card = anchor ? (
    <HoverCard anchor={anchor} detail={detail?.task.id === anchor.taskId ? detail : null} />
  ) : null;

  return { bind, card };
}

/** "2026-10-02" to "10/02/2026", on the STRING — a stage date is a calendar day. */
function shortDay(iso: string | null): string {
  if (!iso) return "—";
  return mdy(iso);
}

/** An instant to a calendar day, for "done 10/02/2026". */
function shortInstant(iso: string): string {
  return shortDay(iso.slice(0, 10));
}

function HoverCard({ anchor, detail }: { anchor: Anchor; detail: TaskDetail | null }) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  /*
   * Measured after render, because where it goes depends on how tall it came
   * out — and how tall it came out depends on how many stages the task has.
   * `useLayoutEffect` so the move happens before the browser paints; in an
   * effect you would see it jump.
   */
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const { width, height } = el.getBoundingClientRect();
    const GAP = 6;

    // 48px in from the row's left edge, so the card sits under the title
    // rather than under the margin, and never off the right of the screen.
    let left = anchor.rect.left + 48;
    left = Math.max(8, Math.min(left, window.innerWidth - width - 8));

    // Below by default; above when below would run off the bottom. A card
    // half off the screen is worse than one on the other side of the row.
    let top = anchor.rect.bottom + GAP;
    if (top + height > window.innerHeight - 8) {
      top = Math.max(8, anchor.rect.top - height - GAP);
    }
    setPos({ top, left });
  }, [anchor, detail]);

  const stages = detail?.stages ?? [];

  /*
   * When each stage was last completed. A stage entered three times has three
   * history entries, so this takes the LATEST — "when was this last finished"
   * is the question, and the first attempt is not the answer.
   */
  const completedAt = new Map<string, string>();
  for (const h of detail?.history ?? []) {
    if (!h.completedAt) continue;
    const seen = completedAt.get(h.stageName);
    if (!seen || h.completedAt > seen) completedAt.set(h.stageName, h.completedAt);
  }

  const current = stages.find((s) => s.isCurrent) ?? null;

  return createPortal(
    <div
      ref={ref}
      className="hovercard"
      style={{
        top: pos?.top ?? anchor.rect.bottom + 6,
        left: pos?.left ?? anchor.rect.left + 48,
        // Hidden until measured, so it is never seen in the wrong place for
        // one frame. Visibility rather than display: it has to be laid out to
        // be measured at all.
        visibility: pos ? "visible" : "hidden",
      }}
      // Not interactive and never announced: it duplicates what the row it
      // describes already says, and a screen reader meeting it twice is worse
      // served than one that never meets it.
      aria-hidden="true"
    >
      <div className="hc-title">
        <KindTag kind="task" size="crumb" />
        {anchor.title}
      </div>

      <div className="hc-facts">
        <span className="hc-label">PM</span>
        <span className="hc-value">{detail?.ownerName ?? "…"}</span>
        <span className="hc-label">Stage due</span>
        <span className="hc-date">{shortDay(current?.dueDate ?? null)}</span>

        <span className="hc-label">With</span>
        <span className="hc-value">
          {current && current.assignees.length > 0
            ? current.assignees.map((a) => a.label).join(", ")
            : detail
              ? "nobody"
              : "…"}
        </span>
        <span className="hc-label">Client due</span>
        <span className="hc-date">
          {shortDay(detail?.deliverable?.clientDueDate ?? detail?.task.dueDate ?? null)}
        </span>
      </div>

      {stages.length > 0 ? (
        <div className="hc-stages">
          {stages.map((s) => (
            <StageLine
              key={s.id}
              stage={s}
              done={completedAt.get(s.name) ?? null}
            />
          ))}
        </div>
      ) : detail ? (
        <div className="hc-stages">
          <div className="hc-none">This task follows no workflow.</div>
        </div>
      ) : null}
    </div>,
    document.body,
  );
}

function StageLine({ stage, done }: { stage: TaskStageView; done: string | null }) {
  return (
    <div className={`hc-stage ${stage.state}`}>
      <span className="hc-dot" />
      <span className="hc-stage-name">{stage.name}</span>
      <span className="hc-stage-who">
        {stage.assignees.length > 0
          ? stage.assignees.map((a) => a.label).join(", ")
          : ""}
      </span>
      <span className="hc-stage-date">
        {/* A finished stage says when it FINISHED; everything else says when
            it is due. The due date of a stage closed last week is not a fact
            anybody needs. */}
        {stage.state === "complete" && done
          ? `done ${shortInstant(done)}`
          : shortDay(stage.dueDate)}
      </span>
    </div>
  );
}
